import React, { useEffect, useState } from 'react';
import ProductCardsD from '../../../../shared/components/ProductCardsD/ProductCardsD';
import { categoryCards } from './CategoryData';
import constructionProductsService from '../../services/construction-products.service';
import { ConstructionProductDto } from '../../dto/construction-products.dto';
import { useCart } from '../../../../contexts/CartContext';
import { useWishlist } from '../../../wishlist/WishlistContext';
import { useProductNavigation } from '../../../../shared/hooks/useProductNavigation';
import 'react-toastify/dist/ReactToastify.css';
import './styles/CategoryBanner.css';
import CategoryBannerMobile from './CategoryBannerMobile';

const CategoryBanner: React.FC = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(categoryCards[0]?.name || '');
  const [products, setProducts] = useState<ConstructionProductDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { addToCart } = useCart();
  const { addItem: addToWishlist, removeItem: removeFromWishlist, isItemInWishlist } = useWishlist();
  const navigateToProduct = useProductNavigation();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    handleCategoryChange(selectedCategory);
  }, [selectedCategory]);

  const handleCategoryChange = async (category: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await constructionProductsService.getProductsByCategoryName(category);
      if (response.success && response.data) {
        setProducts(response.data);
      } else {
        setError('Failed to fetch products');
        setProducts([]);
      }
    } catch (error) {
      setError('Error fetching products');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product: ConstructionProductDto) => {
    try {
      addToCart(product, 1);
    } catch (error) {
    }
  };

  const handleWishlistToggle = (product: ConstructionProductDto) => {
    try {
      const isWishlisted = isItemInWishlist(product._id);
      
      if (isWishlisted) {
        removeFromWishlist(product._id);
      } else {
        const productForWishlist = {
          id: product._id,
          _id: product._id,
          title: product.title || 'Unnamed Product',
          slug: product.slug || `product-${product._id}`,
          description: product.description || '',
          price: product.price || 0,
          compareAtPrice: product.compareAtPrice ?? null,
          quantity: product.quantity ?? 0,
          images: product.images || [],
          imageUrl: product.imageUrl || product.images?.[0]?.url || '',
          isActive: true,
          isFeatured: false,
          hasVariants: false,
          categories: [],
          tags: []
        } as any;

        addToWishlist(productForWishlist);
      }
    } catch (error) {
      // Wishlist update failed silently
    }
  };

  const handleMoreInfo = (product: ConstructionProductDto) => {
    if (product.slug) {
      navigateToProduct(product.slug);
    } else {
    }
  };

  const transformProducts = (apiProducts: ConstructionProductDto[]) => {
    return apiProducts.map(product => ({
      id: parseInt(product._id, 16) || Math.random(),
      name: product.title,
      image: product.imageUrl || product.images?.[0]?.url || 'https://ik.imagekit.io/raghav11/download.jpg?updatedAt=1757934662868',
      alt: product.title,
      slug: product.slug || `product-${product._id}`,
      price: product.price ? `₹${Math.round(product.price)}` : undefined,
      _id: product._id,
      productData: product
    }));
  };

  const productCards = products.length > 0 ? transformProducts(products) : [];

  if (isMobile) {
    return <CategoryBannerMobile/>;
  }
  
  return (
    <div className="category-banner">
      <ProductCardsD 
        categoryCards={categoryCards}
        productCards={productCards}
        heading="Light Up Your Homes in Minutes"
        onCategoryClick={setSelectedCategory}
        loading={loading}
        error={error}
        onAddToCart={handleAddToCart}
        onWishlistToggle={handleWishlistToggle}
        onMoreInfo={handleMoreInfo}
        isItemInWishlist={isItemInWishlist}
        products={products}
      />
    </div>
  );
};

export default CategoryBanner;